/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import {
    object, shape, string, bool, func,
} from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import { compose } from 'recompose';
import Media from 'react-media';
import { bindActionCreators } from 'redux';
// components
import PLASkeleton from '../GraphqlSkeletonComponents/PLASkeleton';
import PDPSkeleton from '../GraphqlSkeletonComponents/PDPSkeleton';
import MobilePDPSkeleton from '../GraphqlSkeletonComponents/MobileSkeleton/MobilePDPSkeleton';
import GraphqlProductPageBuilder from './GraphqlProductPageBuilder';
// redux, hooks, helpers
import { GRAPHQL_ENV } from '../../../gql';
import findProductPageByPartNumber from '../../../gql/queries/findProductPageByPartNumber';
import * as app from '../../../../state/ducks/App';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import { getPassportData } from '../../../../state/ducks/Passport/Passport-Selectors';
import { setPassportBundleData } from '../../../../state/ducks/Passport/Passport-Action';
import CrossMerchContainer from '../CrossMerchContainer/CrossMerchContainer';
import useExperimentServiceAttributes from '../../../helpers/experimentService/useExperimentServiceAttributes';

const brandSelectors = app.ducks.brand.selectors;

const GraphqlProductPageContainerWithPassport = ({
    location, brand, page, presentation_family, productBaseContent, isPlaEnabled, seoData, urlParams,
    isPassportBundleItem, baseProductPartNumber, content, product, isProductListingAd, setBundleData,
    variables, code, refetch, customerData, smallestSkuSelectedEnabled, isPassportAfterCta,
}) => {
    const passportBundle = productBaseContent?.entries?.[0]?.passport_bundle;
    const passportPartNumber = passportBundle?.passportsku?.[0]?.item_sku || '';
    const {
        targeting, context, isGraphqlTargetingEnabled, dynamicPriceInGql,
    } = useExperimentServiceAttributes({ queryName: 'findProductPageByPartNumber' });
    const PRODUCT_BY_PART = findProductPageByPartNumber(brand?.domain, passportPartNumber, dynamicPriceInGql, targeting);
    const { loading, error, data } = useQuery(PRODUCT_BY_PART, { context });
    if (loading) {
        if (isPlaEnabled && isProductListingAd(location.search)) {
            return <PLASkeleton />;
        }
        return (
            <Media
                query="(min-width: 599px)"
            >
                {(matches) => (matches
                    ?  <PDPSkeleton />
                    :  <MobilePDPSkeleton />

                )}
            </Media>
        );
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: PRODUCT_BY_PART,
            component: 'GraphqlProductPageContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }

    if (!data) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: PRODUCT_BY_PART,
            component: 'GraphqlProductPageContainer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        return null;
    }

    const passportProducts = data?.findProductPageByPartNumber?.product?.productSkus;
    const longDescription = data?.findProductPageByPartNumber?.product?.longDescription;
    let isPassportBundleProduct = {};
    if (passportProducts?.length && passportBundle?.passportsku?.[0]) {
        const passportDetails = passportBundle.passportsku[0];
        passportProducts.forEach((sku) => {
            if (sku?.id === passportDetails?.passport_item_sku) {
                const prices = sku?.prices || [];
                prices.forEach((selectedSku) => {
                    if (selectedSku?.type === passportDetails?.price_type) {
                        isPassportBundleProduct = {
                            enable: passportBundle?.check_to_enable_passport_bundle,
                            details: passportBundle?.passportsku?.[0] || {},
                            skuText: passportBundle?.sku_label_text || '',
                            prices: selectedSku,
                            passportSku: sku?.id,
                            desktopImage: passportDetails?.bundle_details?.image_for_pdpage_desktop,
                            mobileImage: passportDetails?.bundle_details?.image_for_pdpage_mobile,
                        };
                    }
                });
            }
        });
    }

    if (isPassportBundleProduct?.enable) {
        const bundleData = {
            enabled: true,
            passportSku: passportPartNumber,
            productSku: productBaseContent?.entries?.[0]?.product_base_code,
            productId: product?.productSkus?.[0]?.id,
            longDescription,
            desktopImage: isPassportBundleProduct?.desktopImage,
            mobileImage: isPassportBundleProduct?.mobileImage,
        };
        setBundleData(bundleData);
    }

    return (
        <div id="product_page_container" data-pagetype="product">
            <CrossMerchContainer
                productBrandId={product.brandId}
                crossMerchBrand={product.brand}
                refetchQuery={() => brand.domain === variables.brand && refetch({ brand: product.brand, partNumber: code })}
            >
                <GraphqlProductPageBuilder
                    content={content}
                    productBaseContent={productBaseContent}
                    seoData={seoData}
                    product={product}
                    urlParams={urlParams}
                    page={page}
                    isPlaEnabled={isPlaEnabled}
                    isPla={isProductListingAd(location.search)}
                    presentation_family={presentation_family}
                    isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                    baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                    isPassportBundleProduct={isPassportBundleProduct || {}}
                    customerData={customerData}
                    smallestSkuSelectedEnabled={smallestSkuSelectedEnabled}
                    isPassportAfterCta={isPassportAfterCta}
                />
            </CrossMerchContainer>
        </div>
    );
};

GraphqlProductPageContainerWithPassport.propTypes = {
    brand: object.isRequired,
    page: shape({
        code: string,
    }).isRequired,
    location: object.isRequired,
    presentation_family: string.isRequired,
    productBaseContent: object.isRequired,
    isPlaEnabled: bool.isRequired,
    seoData: object.isRequired,
    urlParams: object.isRequired,
    isPassportBundleItem: bool.isRequired,
    baseProductPartNumber: object.isRequired,
    content: object.isRequired,
    product: object.isRequired,
    isProductListingAd: object.isRequired,
    setBundleData: func.isRequired,
    variables: shape({
        brand: string,
    }).isRequired,
    code: string.isRequired,
    refetch: func.isRequired,
    customerData: object.isRequired,
    smallestSkuSelectedEnabled: bool,
    isPassportAfterCta: bool,
};
GraphqlProductPageContainerWithPassport.defaultProps = {
    smallestSkuSelectedEnabled: false,
    isPassportAfterCta: false,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    featureFlags: getFeatureFlags(state),
    presentation_family: getPresentationFamily(state),
    passportData: getPassportData(state),
});
const mapDispatchToProps = (dispatch) => ({
    setBundleData: bindActionCreators(setPassportBundleData, dispatch),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
    }),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductPageContainerWithPassport);
